import { Loader2 } from "lucide-react";
import { Button } from "@ascleo/ui/components/ui/button";
import { cn } from "@ascleo/ui/lib/utils";
import type { PublishedStepAction, StepActionTone } from "../context";

const PRIMARY_TONE_CLASSES: Record<StepActionTone, string> = {
  primary: "",
  success: "bg-success text-success-foreground hover:bg-success/90",
  destructive:
    "bg-destructive text-background hover:bg-destructive/90",
};

/**
 * Footer action slot: the step's optional secondary (outline) action and its
 * primary action, both 56px tall, on the right of the shell footer next to
 * "Zurück" (KC-01, KO-08, SPK-03). Renders nothing when the step published no
 * action. A busy action shows a spinner and is not activatable.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * const { primaryAction, secondaryAction } = useSelfService();
 * <StepFooterActions primary={primaryAction} secondary={secondaryAction} />
 * ```
 */
export function StepFooterActions({
  primary,
  secondary,
}: {
  readonly primary: PublishedStepAction | null;
  readonly secondary: PublishedStepAction | null;
}) {
  if (primary === null && secondary === null) return null;
  return (
    <div className="flex min-w-0 flex-wrap items-center justify-end gap-3">
      {secondary ? <StepFooterActionButton action={secondary} variant="outline" /> : null}
      {primary ? <StepFooterActionButton action={primary} variant="default" /> : null}
    </div>
  );
}

function StepFooterActionButton({
  action,
  variant,
}: {
  readonly action: PublishedStepAction;
  readonly variant: "default" | "outline";
}) {
  const Icon = action.icon;
  const busy = action.busy === true;
  const tone = action.tone ?? "primary";
  return (
    <Button
      type="button"
      data-testid={action.testId}
      data-tone={tone}
      variant={variant}
      size="2xl"
      className={cn("px-8", variant === "default" && PRIMARY_TONE_CLASSES[tone])}
      disabled={action.disabled === true || busy}
      aria-busy={busy || undefined}
      onClick={action.onClick}
    >
      {busy ? (
        <Loader2 aria-hidden className="size-6 animate-spin" />
      ) : (
        <Icon aria-hidden className="size-6" />
      )}
      {action.label}
    </Button>
  );
}
